function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { INPUT, LABEL } from "../../components/form-styles";
import { withRouter } from "react-router";
import { CssContainer } from "../../components/container";
import { PageHeader, Card, Pill } from "../../components/analytics-kit";
import { Link } from "react-router-dom";
import { Loader } from "../../components/loaders";
import Error from "../../components/error";
import Auth from "../../functions/authenticate";
import { Button } from "@progress/kendo-react-buttons";
import PlunkDropDown from "../../components/plunk-dropdown";
import SenderPicker from "../email-campaigns/sender-picker";
import ServiceFactory from "../../service";
import account from "../../data/account";
import { hasTenantFeature } from "../../functions/tenant-features";
import { emailSvc } from "./common";
import { loadBrandKit, BRAND_MERGE_FIELDS } from "../../functions/brand-kit";
import Composer, { starterDesign, parseDesign, emptyDesign } from "./composer";
import { authHeaders, apiRoot, errText } from "../reports/common";
import { SubjectSuggest } from "./ai-draft";
import ContentPickList, { CONTENT_PICK_TOP } from "./content-pick-list";

// Content Hub reads (CONTENT_CAMPAIGN_INTEGRATION.md §3.1): the "recommended
// content" picker links this automation to a content_container and can seed the
// draft from the container's email variant.
function contentSvc(api) {
  const Service = ServiceFactory.getType(`/content${api}`, "product"); // /v1 root
  return new Service();
}

/*
 * Create/edit checklist (spec §4): 1 Trigger · 2 Audience · 3 Sender &
 * compliance · 4 Schedule · 5 Content · 6 Review → Activate. Rendered as
 * stacked step cards on one page (the model is single-trigger → delay →
 * single email — no flow canvas).
 *
 * Content: the composer (sections) or raw HTML; the Unlayer visual editor was retired 2026-10-04.
 * OR raw HTML. The design JSON has nowhere to persist yet (only the HTML
 * lands in email_body), so REOPENING an automation edits the HTML — the
 * visual editor starts fresh. Filed as the design-persistence ask.
 *
 * Save mechanics: create = POST DTO. Edit = PUT, which today demands the
 * RAW ENTITY (JSON-string config columns, PascalCase inner keys to match
 * the server's Newtonsoft writes) — the FE composes it and re-stamps
 * Account = tenantId so the row keeps its tenant tag. The "PUT should
 * accept the POST DTO" ask stands.
 */

const TRIGGER_KINDS = [["ProductViewed", "Products viewed", "Visitors who viewed products but didn't convert."], ["CartAbandoned", "Cart abandoned", "Visitors who added to cart but didn't purchase. (Today this reaches the viewed-product population: real cart events aren't wired in yet.)"], ["CustomerLoyalty", "Repeat customer", "Customers with multiple purchases."], ["WelcomeSeries", "New contact", "New contacts with an email address: welcomed once, ever."], ["custom", "Custom event", "Any conversion or engagement event your site tracks."]];
function kindToTypes(kind, eventCategory) {
  switch (kind) {
    case "CartAbandoned":
      return {
        type: "CartAbandonment",
        triggerType: "CartAbandoned"
      };
    case "ProductViewed":
      return {
        type: "CustomTrigger",
        triggerType: "ProductViewed"
      };
    case "WelcomeSeries":
      return {
        type: "WelcomeSeries",
        triggerType: "WelcomeSeries"
      };
    case "CustomerLoyalty":
      return {
        type: "CustomerLoyalty",
        triggerType: "CustomerLoyalty"
      };
    default:
      return {
        type: "CustomTrigger",
        triggerType: eventCategory || "Engagement"
      };
  }
}
function typesToKind(a) {
  if (!a) return "ProductViewed";
  if (a.type === "CartAbandonment") return "CartAbandoned";
  if (a.type === "WelcomeSeries") return "WelcomeSeries";
  if (a.type === "CustomerLoyalty") return "CustomerLoyalty";
  if (a.type === "CustomTrigger" && a.triggerType === "ProductViewed") return "ProductViewed";
  return "custom";
}
function footerHtml(s) {
  const addr = [s.mailingAddress, s.city, s.state, s.postalCode, s.country].filter(Boolean).join(", ");
  return `<div style="font-size:12px;color:#888;text-align:center;padding:16px 0;border-top:1px solid var(--plk-border-subtle);margin-top:24px;">` + `${s.companyName || ""}${addr ? " · " + addr : ""}` + `${s.website ? ` · <a href="${s.website}" style="color:#888;">${s.website}</a>` : ""}` + `<br/>You're receiving this because you visited our site. <a href="{{UnsubscribeUrl}}" style="color:#888;">Unsubscribe</a></div>`;
}
const MERGE_CHIPS = [["{{FirstName}}", "Contact first name"], ["{{Products}}", "Products from the trigger event"], ["{{UnsubscribeUrl}}", "Unsubscribe link (required by CAN-SPAM; added for you if missing)"], ...BRAND_MERGE_FIELDS];
class EmailAutomationEditorComponent extends React.Component {
  constructor(props) {
    super(props);
    // Composer preview: the server render (CampaignService POST render) with sample
    // data — the same renderer the worker uses at send time. opts.personId renders
    // for that real contact ("Preview as…").
    _defineProperty(this, "renderPreview", async (design, opts) => {
      const body = {
        design,
        firstName: "Dana"
      };
      if (opts && opts.personId) {
        body.personId = opts.personId;
        delete body.firstName;
      }
      const res = await fetch(`${apiRoot()}/campaign/email/render`, {
        method: "POST",
        headers: {
          ...authHeaders(),
          "content-type": "application/json"
        },
        body: JSON.stringify(body)
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || `Preview failed (${res.status}).`);
      return d;
    });
    _defineProperty(this, "pickStarter", kind => this.setState({
      design: starterDesign(kind)
    }));
    // ---- Content Hub link (§3.1) ----------------------------------------
    _defineProperty(this, "toggleContentPicker", () => {
      const open = !this.state.contentPickerOpen;
      this.setState({
        contentPickerOpen: open
      });
      if (open && this.state.contentPicks == null) this.loadContentPicks();
    });
    _defineProperty(this, "loadContentPicks", async () => {
      const d = await contentSvc(`/containers/this_quarter?$top=${CONTENT_PICK_TOP}`).getAsync().catch(() => null);
      if (!this._alive) return;
      const items = d && d.items || [];
      // Rank in the content tier currency: sales, then conversions, then sessions.
      const metric = c => Number(c.salesValue || 0) * 1e6 + Number(c.conversions || 0) * 1e3 + Number(c.sessions || 0);
      items.sort((a, b) => metric(b) - metric(a));
      this.setState({
        contentPicks: items
      });
    });
    _defineProperty(this, "pickContainer", c => {
      this.setState({
        containerId: c.id,
        containerTitle: c.title || "",
        contentPickerOpen: false,
        seedNote: null
      });
      // Seed the draft from the container's email variant only when the body is
      // still empty — never overwrite work in progress.
      const emptyBody = this.state.bodyMode === "compose" ? !(this.state.design.sections || []).length : !(this.state.bodyHtml || "").trim();
      if (!emptyBody) return;
      contentSvc(`/containers/detail/${c.id}`).getAsync().then(d => {
        if (!this._alive || !d) return;
        const email = (d.variants || []).find(v => v.channel === "email" && (v.body || "").trim());
        if (email) {
          this.setState({
            bodyMode: "html",
            bodyHtml: email.body,
            seedNote: `Seeded the draft from "${c.title || "content"}": edit freely.`
          });
        }
      }).catch(() => {});
    });
    _defineProperty(this, "unlinkContainer", () => this.setState({
      containerId: null,
      containerTitle: "",
      seedNote: null
    }));
    // "Send test to me" (EMAIL_EDITOR_BRANDING.md step 4): server-rendered like
    // the preview, sent to the signed-in user now — nothing saved, no recipient row.
    _defineProperty(this, "sendTest", async () => {
      const st = this.state;
      const me = account.getEmail && account.getEmail() || "";
      const body = {
        subject: st.subject || "",
        email: me || null,
        fromName: st.fromName,
        fromEmail: st.fromEmail,
        firstName: "Dana"
      };
      if (st.bodyMode === "compose") {
        if (!(st.design.sections || []).length) {
          this.setState({
            testNote: {
              kind: "error",
              text: "Add at least one section first."
            }
          });
          return;
        }
        body.design = st.design;
      } else {
        const html = await this.exportBody();
        if (!(html || "").trim()) {
          this.setState({
            testNote: {
              kind: "error",
              text: "The email body is empty."
            }
          });
          return;
        }
        body.html = html;
      }
      this.setState({
        testBusy: true,
        testNote: null
      });
      try {
        const res = await fetch(`${apiRoot()}/campaign/email/test-send`, {
          method: "POST",
          headers: {
            ...authHeaders(),
            "content-type": "application/json"
          },
          body: JSON.stringify(body)
        });
        const d = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(d.error || `Test send failed (${res.status}).`);
        if (this._alive) this.setState({
          testBusy: false,
          testNote: {
            kind: "info",
            text: `Sent to ${d.to || me || "you"}: subject "${d.subject || body.subject}".`
          }
        });
      } catch (e) {
        if (this._alive) this.setState({
          testBusy: false,
          testNote: {
            kind: "error",
            text: e && e.message || "Test send failed."
          }
        });
      }
    });
    _defineProperty(this, "save", async activate => {
      const err = this.validate();
      if (err) {
        this.setState({
          error: err,
          confirmActivate: false
        });
        window.scrollTo(0, 0);
        return;
      }
      this.setState({
        saving: true,
        error: null
      });
      const st = this.state;
      const body = await this.exportBody();
      const {
        type,
        triggerType,
        trigger,
        schedule,
        sender
      } = this.buildConfigs();
      try {
        let id = this.id();
        if (!id) {
          const created = await emailSvc("/automations").postAsync({
            name: st.name.trim(),
            type,
            triggerType,
            triggerConfig: trigger,
            scheduleConfig: schedule,
            senderConfig: sender,
            audienceInclude: st.include,
            audienceExclude: st.exclude,
            emailSubject: st.subject.trim(),
            emailBody: body,
            emailTemplateId: null,
            designJson: st.bodyMode === "compose" ? JSON.stringify(st.design) : null,
            containerId: st.containerId || null
          });
          id = created && created.id;
          if (!id) throw new Error("Create returned no id");
        } else {
          // PUT wants the raw entity — string config columns, inner
          // keys PascalCase (matches the server's own writes), Account
          // re-stamped so the row keeps its tenant tag.
          await emailSvc(`/automations/${id}`).putAsync({
            id,
            name: st.name.trim(),
            status: st.status,
            type,
            triggerType,
            triggerConfig: JSON.stringify(trigger),
            audienceInclude: JSON.stringify(st.include),
            audienceExclude: JSON.stringify(st.exclude),
            scheduleConfig: JSON.stringify(schedule),
            senderConfig: JSON.stringify(sender),
            emailSubject: st.subject.trim(),
            emailBody: body,
            emailTemplateId: null,
            containerId: st.containerId || 0,
            // 0 = unlink (partial-update semantics)
            account: (account.get() || {}).tenantId || "",
            modifiedDate: new Date().toISOString()
          });
        }
        if (activate) await emailSvc(`/automations/${id}/status?status=Running`).putAsync({});
        if (this._alive) this.props.history.push(`/Email/automations/detail/${id}`);
      } catch (e) {
        if (this._alive) this.setState({
          saving: false,
          error: errText(e, "Save failed: try again."),
          confirmActivate: false
        });
        window.scrollTo(0, 0);
      }
    });
    this.state = {
      ready: !props.params.id,
      notFound: false,
      name: "",
      kind: "ProductViewed",
      eventName: "",
      eventCategory: "Engagement",
      eventFreeText: false,
      railCorrected: null,
      lookbackDays: "",
      excludeConverted: true,
      audienceList: null,
      include: [],
      exclude: [],
      fromName: "",
      fromEmail: "",
      companyName: "",
      website: "",
      mailingAddress: "",
      city: "",
      stateField: "",
      postalCode: "",
      country: "US",
      complianceOverride: false,
      sendImmediately: false,
      delayDays: 0,
      delayHours: 1,
      delayMinutes: 0,
      subject: "",
      bodyMode: "compose",
      bodyHtml: "",
      design: emptyDesign(),
      triggers: null,
      status: "Draft",
      brand: null,
      brandPrefilled: false,
      saving: false,
      error: null,
      confirmActivate: false,
      testBusy: false,
      testNote: null,
      containerId: null,
      containerTitle: "",
      contentPicks: null,
      contentPickerOpen: false,
      seedNote: null
    };
    this._alive = true;
  }
  id() {
    return this.props.params.id || null;
  }
  async componentDidMount() {
    loadBrandKit().then(b => {
      if (!this._alive || !b) return;
      const company = b.company || b.defaultCompany || (account.get() || {}).company || "";
      const patch = {
        brand: b
      };
      // New automation: sender + compliance details come from the brand kit
      // (step 1). Existing ones keep whatever was saved.
      if (!this.id()) {
        patch.fromName = this.state.fromName || company;
        patch.companyName = this.state.companyName || company;
        patch.website = this.state.website || b.website || "";
        patch.mailingAddress = this.state.mailingAddress || b.mailingAddress || "";
        patch.fromEmail = this.state.fromEmail || "no-reply@plunk.io";
        patch.brandPrefilled = !!(company || b.website || b.mailingAddress);
      }
      this.setState(patch);
    });
    if (!(Auth.isAuthenticated() || (await Auth.attachOrSignIn()))) return;
    // Trigger catalog + audiences in parallel; both are decorations the
    // editor works without.
    emailSvc("/triggers").getAsync().then(t => this._alive && this.setState({
      triggers: t || null
    }, () => this.reconcileRail())).catch(() => {});
    try {
      const AudSvc = ServiceFactory.getType("/audience", "audience");
      const d = await new AudSvc().getAsync();
      const list = (Array.isArray(d) ? d : d && d.items || []).map(a => ({
        id: String(a.id),
        name: a.name || a.title || String(a.id)
      }));
      if (this._alive) this.setState({
        audienceList: list
      });
    } catch (e) {
      if (this._alive) this.setState({
        audienceList: []
      });
    }
    if (this.id()) {
      try {
        const a = await emailSvc(`/automations/${this.id()}?range=DAY`).getAsync();
        if (!this._alive) return;
        if (!a) {
          this.setState({
            notFound: true,
            ready: true
          });
          return;
        }
        const tc = a.triggerConfig || {};
        const sc = a.scheduleConfig || {};
        const sd = a.senderConfig || {};
        this.setState({
          ready: true,
          name: a.name || "",
          status: a.status || "Draft",
          kind: typesToKind(a),
          eventName: tc.eventName || "",
          eventCategory: a.triggerType === "Conversion" ? "Conversion" : "Engagement",
          lookbackDays: tc.lookbackDays != null ? String(tc.lookbackDays) : "",
          excludeConverted: tc.excludeConverted !== false,
          include: a.audienceInclude || [],
          exclude: a.audienceExclude || [],
          fromName: sd.fromName || "",
          fromEmail: sd.fromEmail || "",
          companyName: sd.companyName || "",
          website: sd.website || "",
          mailingAddress: sd.mailingAddress || "",
          city: sd.city || "",
          stateField: sd.state || "",
          postalCode: sd.postalCode || "",
          country: sd.country || "US",
          sendImmediately: !!sc.sendImmediately,
          delayDays: sc.delayDays || 0,
          delayHours: sc.delayHours || 0,
          delayMinutes: sc.delayMinutes || 0,
          subject: a.emailSubject || "",
          design: parseDesign(a.designJson) || emptyDesign(),
          bodyMode: parseDesign(a.designJson) ? "compose" : "html",
          bodyHtml: a.emailBody || "",
          containerId: a.containerId || null
        }, () => this.reconcileRail());
        // Resolve the linked container's title for the chip (best-effort).
        if (a.containerId) {
          contentSvc(`/containers/detail/${a.containerId}`).getAsync().then(d => this._alive && d && d.container && this.setState({
            containerTitle: d.container.title || ""
          })).catch(() => {});
        }
      } catch (e) {
        if (this._alive) this.setState({
          notFound: true,
          ready: true
        });
      }
    }
  }
  componentWillUnmount() {
    this._alive = false;
  }

  // §288.2 with teeth: the rail is derived from WHICH catalog list the
  // event came from (conversion names live on page_conversion, engagement
  // names on page_engagement) — never a field, never a default. Returns
  // null for events the site hasn't emitted yet.
  railOf(name) {
    const t = this.state.triggers;
    if (!t || !name) return null;
    if ((t.conversions || []).some(x => x.name === name)) return "Conversion";
    if ((t.engagements || []).some(x => x.name === name)) return "Engagement";
    return null;
  }
  // Heals mis-railed configs (the three DS activated that could never
  // fire): once both the catalog and an edited automation are loaded, a
  // catalog event carrying the wrong category snaps to its real rail —
  // surfaced as a hint, persisted on the next save.
  reconcileRail() {
    const st = this.state;
    if (st.kind !== "custom" || !st.eventName) return;
    const rail = this.railOf(st.eventName);
    if (rail && rail !== st.eventCategory) this.setState({
      eventCategory: rail,
      railCorrected: rail
    });
  }
  exportBody() {
    // Compose mode: a rendered copy goes into email_body (detail preview +
    // pre-composer paths); the worker re-renders from design_json per send.
    if (this.state.bodyMode === "compose") {
      return this.renderPreview(this.state.design).then(d => d && d.html || this.state.bodyHtml).catch(() => this.state.bodyHtml);
    }
    // HTML mode is the textarea verbatim.
    return Promise.resolve(this.state.bodyHtml);
  }

  // ---- Save ----------------------------------------------------------
  buildConfigs() {
    const st = this.state;
    const {
      type,
      triggerType
    } = kindToTypes(st.kind, st.eventCategory);
    const trigger = {
      EventName: st.kind === "custom" ? st.eventName.trim() : st.kind === "WelcomeSeries" ? "ContactCreated" : st.kind,
      EventCategory: st.kind === "custom" ? st.eventCategory : null,
      LookbackDays: st.lookbackDays !== "" && !isNaN(parseInt(st.lookbackDays, 10)) ? parseInt(st.lookbackDays, 10) : null,
      ExcludeConverted: !!st.excludeConverted
    };
    const schedule = {
      SendImmediately: !!st.sendImmediately,
      DelayDays: Number(st.delayDays) || 0,
      DelayHours: Number(st.delayHours) || 0,
      DelayMinutes: Number(st.delayMinutes) || 0
    };
    const sender = {
      FromName: st.fromName,
      FromEmail: st.fromEmail,
      CompanyName: st.companyName,
      Website: st.website,
      MailingAddress: st.mailingAddress,
      City: st.city,
      State: st.stateField,
      PostalCode: st.postalCode,
      Country: st.country
    };
    return {
      type,
      triggerType,
      trigger,
      schedule,
      sender
    };
  }
  validate() {
    const st = this.state;
    if (!st.name.trim()) return "Give the automation a name.";
    if (st.kind === "custom" && !st.eventName.trim()) return "Pick or enter the event that triggers this automation.";
    if (!st.subject.trim()) return "Add an email subject.";
    if (!st.fromEmail.trim() || !/.+@.+\..+/.test(st.fromEmail)) return "A valid From email is required.";
    if (st.bodyMode === "compose" && !(st.design.sections || []).length) return "Add at least one section to the email (or pick a starter).";
    return null;
  }
  // ---- Render helpers --------------------------------------------------
  step(n, title, children, hint) {
    return /*#__PURE__*/React.createElement(Card, {
      title: `${n} · ${title}`,
      hint: hint
    }, children);
  }
  audienceChips(field) {
    const {
      audienceList
    } = this.state;
    const selected = this.state[field];
    if (audienceList === null) return /*#__PURE__*/React.createElement("p", {
      className: "territory-hint",
      style: {
        margin: 0
      }
    }, "Loading audiences\u2026");
    if (!audienceList.length) return /*#__PURE__*/React.createElement("p", {
      className: "territory-hint",
      style: {
        margin: 0
      }
    }, "No audiences configured: this automation applies to all triggered contacts.");
    return /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        flexWrap: "wrap",
        gap: "6px"
      }
    }, audienceList.map(a => {
      const on = selected.includes(a.id);
      return /*#__PURE__*/React.createElement("a", {
        key: a.id,
        className: "mtf-chip" + (on ? " on" : ""),
        style: {
          cursor: "pointer"
        },
        onClick: () => this.setState({
          [field]: on ? selected.filter(x => x !== a.id) : [...selected, a.id]
        })
      }, a.name);
    }));
  }
  render() {
    if (!hasTenantFeature("emailAutomation")) {
      return /*#__PURE__*/React.createElement(CssContainer, {
        cls: "margin-squeeze"
      }, /*#__PURE__*/React.createElement("p", {
        className: "module-empty"
      }, "Email Automation isn't configured for this workspace."));
    }
    const st = this.state;
    if (!st.ready) return /*#__PURE__*/React.createElement(Loader, null);
    if (st.notFound) return /*#__PURE__*/React.createElement(Error, {
      message: "This automation could not be loaded."
    });
    const editing = !!this.id();
    const triggers = st.triggers || {};
    const dynamicEvents = [...(triggers.conversions || []), ...(triggers.engagements || [])];
    const inputStyle = INPUT;
    const fieldCol = {
      flex: "1 1 220px",
      minWidth: "180px"
    };
    const labelStyle = LABEL;
    const senderPreview = footerHtml({
      companyName: st.companyName,
      mailingAddress: st.mailingAddress,
      city: st.city,
      state: st.stateField,
      postalCode: st.postalCode,
      country: st.country,
      website: st.website
    });
    return /*#__PURE__*/React.createElement("div", {
      className: "territory-view wa-view wa-editor"
    }, /*#__PURE__*/React.createElement(PageHeader, {
      crumbs: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Link, {
        to: "/Email/journeys"
      }, "Journeys"), " \xB7 ", /*#__PURE__*/React.createElement(Link, {
        to: "/Email/automations"
      }, "Automations"), " \xB7 ", editing ? "Edit" : "New"),
      title: /*#__PURE__*/React.createElement(React.Fragment, null, editing ? st.name || "Edit automation" : "New automation", " ", editing && st.status === "Running" ? /*#__PURE__*/React.createElement(Pill, {
        tone: "success"
      }, "running") : null),
      subtitle: editing ? "Changes apply to contacts triggered after you save; contacts already sent are never re-sent." : "One triggered email: pick the trigger, the audience, the sender, then write the email and review."
    }), st.error ? /*#__PURE__*/React.createElement("div", {
      className: "wa-notice danger"
    }, st.error) : null, /*#__PURE__*/React.createElement(Card, {
      title: "Name",
      hint: "how it appears in the list"
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        maxWidth: 480
      }
    }, /*#__PURE__*/React.createElement("input", {
      className: "cust-search wa-search",
      style: {
        width: "100%"
      },
      value: st.name,
      placeholder: "e.g. Viewed-product follow-up",
      onChange: e => this.setState({
        name: e.target.value
      })
    }))), this.step(1, "Trigger", /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        flexWrap: "wrap",
        gap: "6px",
        marginBottom: "10px"
      }
    }, TRIGGER_KINDS.map(([k, label, desc]) => /*#__PURE__*/React.createElement("a", {
      key: k,
      className: "mtf-chip" + (st.kind === k ? " on" : ""),
      style: {
        cursor: "pointer"
      },
      title: desc,
      onClick: () => this.setState({
        kind: k
      })
    }, label))), /*#__PURE__*/React.createElement("p", {
      className: "territory-hint",
      style: {
        margin: "0 0 10px"
      }
    }, (TRIGGER_KINDS.find(([k]) => k === st.kind) || [])[2]), st.kind === "custom" && (() => {
      // §283: picker fed by the live trigger catalog, grouped by rail;
      // free text stays possible for events that haven't fired yet.
      const knownRail = this.railOf(st.eventName);
      const freeText = st.eventFreeText || !!st.eventName && dynamicEvents.length > 0 && !knownRail;
      const options = [["", "Choose an event…"], ...(triggers.conversions || []).map(t => [t.name, `${t.name}: conversion`]), ...(triggers.engagements || []).map(t => [t.name, `${t.name}: engagement`]), ["__custom", "Type a custom event…"]];
      return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
        style: {
          display: "flex",
          gap: "12px",
          flexWrap: "wrap",
          marginBottom: "6px",
          alignItems: "flex-end"
        }
      }, /*#__PURE__*/React.createElement("div", {
        style: fieldCol
      }, /*#__PURE__*/React.createElement("label", {
        style: labelStyle
      }, "Event name"), dynamicEvents.length ? /*#__PURE__*/React.createElement(PlunkDropDown, {
        width: "100%",
        value: freeText ? "__custom" : st.eventName,
        options: options,
        onChange: v => {
          if (v === "__custom") {
            this.setState({
              eventFreeText: true,
              eventName: "",
              railCorrected: null
            });
            return;
          }
          this.setState({
            eventFreeText: false,
            eventName: v,
            eventCategory: this.railOf(v) || "Engagement",
            railCorrected: null
          });
        }
      }) : /*#__PURE__*/React.createElement("input", {
        style: inputStyle,
        value: st.eventName,
        placeholder: "Exact event name from your site tracking",
        onChange: e => this.setState({
          eventName: e.target.value
        })
      })), freeText && dynamicEvents.length ? /*#__PURE__*/React.createElement("div", {
        style: fieldCol
      }, /*#__PURE__*/React.createElement("label", {
        style: labelStyle
      }, "Custom event name"), /*#__PURE__*/React.createElement("input", {
        style: inputStyle,
        value: st.eventName,
        placeholder: "Exact event name from your site tracking",
        onChange: e => this.setState({
          eventName: e.target.value
        })
      })) : null, freeText || !dynamicEvents.length ? /*#__PURE__*/React.createElement("div", {
        style: {
          flex: "0 0 190px"
        }
      }, /*#__PURE__*/React.createElement("label", {
        style: labelStyle
      }, "Event rail"), /*#__PURE__*/React.createElement(PlunkDropDown, {
        width: "190px",
        value: st.eventCategory,
        options: [["Engagement", "Engagement"], ["Conversion", "Conversion"]],
        onChange: v => this.setState({
          eventCategory: v
        })
      })) : knownRail ? /*#__PURE__*/React.createElement("span", {
        className: "cust-stage",
        style: {
          marginBottom: "7px",
          background: knownRail === "Conversion" ? "var(--plk-success-soft)" : "var(--plk-surface-alt)",
          color: knownRail === "Conversion" ? "var(--plk-success-text)" : "var(--plk-text-dim)"
        }
      }, knownRail.toLowerCase(), " event") : null), freeText && st.eventName && dynamicEvents.length ? /*#__PURE__*/React.createElement("p", {
        className: "territory-hint",
        style: {
          margin: "0 0 10px",
          color: "var(--plk-warning-text)"
        }
      }, "\"", st.eventName, "\" hasn't been seen on your site yet: the automation arms and waits for it. Pick the rail it will fire on (conversion vs engagement); a wrong rail never triggers.") : null, st.railCorrected ? /*#__PURE__*/React.createElement("p", {
        className: "territory-hint",
        style: {
          margin: "0 0 10px",
          color: "var(--plk-warning-text)"
        }
      }, "Event rail corrected to ", /*#__PURE__*/React.createElement("b", null, st.railCorrected), " to match how \"", st.eventName, "\" actually fires on your site: it was mis-set, so this automation could never trigger. Save to persist the fix.") : null);
    })(), st.kind === "custom" ? /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        gap: "16px",
        alignItems: "center",
        flexWrap: "wrap"
      }
    }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("label", {
      style: labelStyle
    }, "Lookback window (days)"), /*#__PURE__*/React.createElement("input", {
      type: "number",
      min: "1",
      style: {
        ...inputStyle,
        width: "110px"
      },
      value: st.lookbackDays,
      placeholder: "7",
      onChange: e => this.setState({
        lookbackDays: e.target.value
      })
    })), /*#__PURE__*/React.createElement("label", {
      style: {
        fontSize: "13px",
        display: "inline-flex",
        alignItems: "center",
        gap: "8px",
        marginTop: "14px",
        whiteSpace: "nowrap"
      }
    }, /*#__PURE__*/React.createElement("input", {
      type: "checkbox",
      style: {
        width: "auto",
        margin: 0,
        flex: "0 0 auto"
      },
      checked: st.excludeConverted,
      onChange: e => this.setState({
        excludeConverted: e.target.checked
      })
    }), /*#__PURE__*/React.createElement("span", null, "Skip contacts who already converted"))) : /*#__PURE__*/React.createElement("p", {
      className: "territory-hint",
      style: {
        margin: 0
      }
    }, "Checked every 15 minutes over a rolling 7-day window (built-in triggers don't take a lookback)."))), this.step(2, "Audience", /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("label", {
      style: labelStyle
    }, "Include only these audiences (none = everyone triggered)"), this.audienceChips("include"), /*#__PURE__*/React.createElement("div", {
      style: {
        height: "12px"
      }
    }), /*#__PURE__*/React.createElement("label", {
      style: labelStyle
    }, "Exclude these audiences"), this.audienceChips("exclude"))), this.step(3, "Sender & compliance", /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(SenderPicker, {
      fromName: st.fromName,
      fromEmail: st.fromEmail,
      inputStyle: inputStyle,
      labelStyle: labelStyle,
      onPick: p => this.setState({
        fromName: p.fromName,
        fromEmail: p.fromEmail
      })
    }), /*#__PURE__*/React.createElement("p", {
      className: "territory-hint",
      style: {
        margin: "12px 0 6px"
      }
    }, /*#__PURE__*/React.createElement("b", null, "Compliance footer:"), " company name, mailing address and website come from your ", /*#__PURE__*/React.createElement("a", {
      href: "/Settings/Brand"
    }, "brand kit"), " and are added at send time with the unsubscribe link (CAN-SPAM) whenever the email has no unsubscribe link of its own. Place ", /*#__PURE__*/React.createElement("code", null, "{{UnsubscribeUrl}}"), " (or \"Insert footer\") to design your own instead.", " ", /*#__PURE__*/React.createElement("a", {
      onClick: () => this.setState({
        complianceOverride: !st.complianceOverride
      })
    }, st.complianceOverride ? "Hide override" : "Override for this automation only")), st.complianceOverride ? /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        gap: "12px",
        flexWrap: "wrap"
      }
    }, [["companyName", "Company name"], ["website", "Website"], ["mailingAddress", "Mailing address"], ["city", "City"], ["stateField", "State"], ["postalCode", "Postal code"]].map(([f, label]) => /*#__PURE__*/React.createElement("div", {
      key: f,
      style: fieldCol
    }, /*#__PURE__*/React.createElement("label", {
      style: labelStyle
    }, label), /*#__PURE__*/React.createElement("input", {
      style: inputStyle,
      value: st[f],
      onChange: e => this.setState({
        [f]: e.target.value
      })
    })))) : null, /*#__PURE__*/React.createElement("div", {
      style: {
        border: "1px dashed var(--plk-border)",
        borderRadius: "6px",
        padding: "4px 10px",
        background: "var(--plk-surface-alt)"
      },
      dangerouslySetInnerHTML: {
        __html: senderPreview
      }
    }))), this.step(4, "Schedule", /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        gap: "18px",
        alignItems: "center",
        flexWrap: "wrap"
      }
    }, /*#__PURE__*/React.createElement("label", {
      style: {
        fontSize: "13px",
        display: "inline-flex",
        alignItems: "center",
        gap: "8px",
        whiteSpace: "nowrap"
      }
    }, /*#__PURE__*/React.createElement("input", {
      type: "checkbox",
      style: {
        width: "auto",
        margin: 0,
        flex: "0 0 auto"
      },
      checked: st.sendImmediately,
      onChange: e => this.setState({
        sendImmediately: e.target.checked
      })
    }), /*#__PURE__*/React.createElement("span", null, "Send immediately when triggered")), !st.sendImmediately && /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        gap: "10px",
        alignItems: "flex-end"
      }
    }, [["delayDays", "Days"], ["delayHours", "Hours"], ["delayMinutes", "Minutes"]].map(([f, label]) => /*#__PURE__*/React.createElement("div", {
      key: f
    }, /*#__PURE__*/React.createElement("label", {
      style: labelStyle
    }, label), /*#__PURE__*/React.createElement("input", {
      type: "number",
      min: "0",
      style: {
        ...inputStyle,
        width: "80px"
      },
      value: st[f],
      onChange: e => this.setState({
        [f]: e.target.value
      })
    }))), /*#__PURE__*/React.createElement("span", {
      style: {
        fontSize: "13px",
        paddingBottom: "8px"
      }
    }, "after the trigger")))), this.step(5, "Content", /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      style: {
        maxWidth: "520px",
        marginBottom: "10px"
      }
    }, /*#__PURE__*/React.createElement("label", {
      style: labelStyle
    }, "Subject"), /*#__PURE__*/React.createElement("input", {
      style: inputStyle,
      value: st.subject,
      placeholder: "e.g. Still thinking it over?",
      onChange: e => this.setState({
        subject: e.target.value
      })
    }), /*#__PURE__*/React.createElement(SubjectSuggest, {
      kind: st.kind,
      triggerType: kindToTypes(st.kind, st.eventCategory).triggerType,
      subject: st.subject,
      onUse: p => this.setState({
        subject: p.subject || st.subject
      })
    })), /*#__PURE__*/React.createElement("div", {
      style: {
        marginBottom: "10px"
      }
    }, /*#__PURE__*/React.createElement("label", {
      style: labelStyle
    }, "Content Hub link"), st.containerId ? /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        flexWrap: "wrap"
      }
    }, /*#__PURE__*/React.createElement("a", {
      className: "cust-stage",
      style: {
        background: "var(--plk-info-soft)",
        color: "var(--plk-info-text)"
      }
    }, st.containerTitle || `Content #${st.containerId}`), /*#__PURE__*/React.createElement("a", {
      className: "territory-toggle",
      style: {
        cursor: "pointer"
      },
      onClick: this.unlinkContainer
    }, "Unlink"), /*#__PURE__*/React.createElement("span", {
      className: "territory-hint",
      style: {
        margin: 0
      }
    }, "Performance from this automation flows into this content's funnel.")) : /*#__PURE__*/React.createElement("a", {
      className: "territory-toggle",
      style: {
        cursor: "pointer"
      },
      onClick: this.toggleContentPicker
    }, st.contentPickerOpen ? "Close" : "Link recommended content →"), st.contentPickerOpen && !st.containerId && /*#__PURE__*/React.createElement("div", {
      style: {
        border: "1px dashed var(--plk-border)",
        borderRadius: "8px",
        padding: "10px 12px",
        marginTop: "8px",
        maxWidth: "620px"
      }
    }, /*#__PURE__*/React.createElement("p", {
      className: "territory-hint",
      style: {
        margin: "0 0 8px"
      }
    }, "Your best-performing content this quarter: link one so this automation contributes to its funnel, and seed the draft from its email copy."), /*#__PURE__*/React.createElement(ContentPickList, {
      items: st.contentPicks,
      onPick: c => this.pickContainer(c)
    })), st.seedNote && /*#__PURE__*/React.createElement("p", {
      className: "territory-hint",
      style: {
        margin: "6px 0 0",
        color: "var(--plk-text-dim)"
      }
    }, st.seedNote)), /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        gap: "10px",
        alignItems: "center",
        marginBottom: "6px",
        flexWrap: "wrap"
      }
    }, /*#__PURE__*/React.createElement("div", {
      className: "mdt-tabs",
      style: {
        marginBottom: 0
      }
    }, [["compose", "Compose"], ["html", "HTML"]].map(([v, l]) => /*#__PURE__*/React.createElement("a", {
      key: v,
      className: "mdt-tab" + (st.bodyMode === v ? " on" : ""),
      style: {
        cursor: "pointer"
      },
      onClick: () => this.setState({
        bodyMode: v
      })
    }, l))), /*#__PURE__*/React.createElement("span", {
      style: {
        marginLeft: "auto",
        flexWrap: "wrap",
        display: "inline-flex",
        gap: 8,
        alignItems: "center"
      }
    }, st.bodyMode !== "compose" ? /*#__PURE__*/React.createElement(React.Fragment, null, st.testNote ? /*#__PURE__*/React.createElement("span", {
      style: {
        fontSize: 12,
        color: st.testNote.kind === "error" ? "var(--plk-danger-text)" : "var(--plk-text-dim)"
      }
    }, st.testNote.text) : null, /*#__PURE__*/React.createElement(Button, {
      disabled: st.testBusy,
      onClick: this.sendTest,
      title: "Emails this automation to you, rendered with sample data: nothing is saved"
    }, st.testBusy ? "Sending…" : "Send test to me")) : null)), /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        gap: "6px",
        alignItems: "center",
        marginBottom: "8px",
        flexWrap: "wrap"
      }
    }, /*#__PURE__*/React.createElement("span", {
      style: {
        fontSize: "12px",
        color: "var(--plk-text-dim)"
      }
    }, "Merge fields:"), MERGE_CHIPS.map(([chip, tip]) => /*#__PURE__*/React.createElement("a", {
      key: chip,
      className: "mtf-chip",
      style: {
        cursor: "pointer"
      },
      title: `${tip}: click to copy`,
      onClick: () => {
        try {
          navigator.clipboard.writeText(chip);
        } catch (e) {/* noop */}
      }
    }, chip))), st.bodyMode === "compose" ? /*#__PURE__*/React.createElement(Composer, {
      design: st.design,
      brand: st.brand,
      kind: st.kind,
      triggerType: kindToTypes(st.kind, st.eventCategory).triggerType,
      onChange: design => this.setState({
        design
      }),
      onPickStarter: this.pickStarter,
      renderPreview: this.renderPreview,
      onSendTest: this.sendTest,
      testBusy: st.testBusy,
      testNote: st.testNote
    }) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("textarea", {
      value: st.bodyHtml,
      onChange: e => this.setState({
        bodyHtml: e.target.value
      }),
      style: {
        ...inputStyle,
        minHeight: "260px",
        fontFamily: "monospace",
        fontSize: "12px",
        resize: "vertical"
      },
      placeholder: "<html> email body\u2026 include the compliance footer."
    }), /*#__PURE__*/React.createElement("div", {
      style: {
        marginTop: "6px"
      }
    }, /*#__PURE__*/React.createElement(Button, {
      onClick: () => this.setState({
        bodyHtml: st.bodyHtml + "\n" + senderPreview
      })
    }, "Insert compliance footer"))))), this.step(6, "Review & activate", /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("table", {
      className: "territory-table",
      style: {
        maxWidth: "640px"
      }
    }, /*#__PURE__*/React.createElement("tbody", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", {
      className: "name"
    }, "Trigger"), /*#__PURE__*/React.createElement("td", null, (TRIGGER_KINDS.find(([k]) => k === st.kind) || [])[1], st.kind === "custom" && st.eventName ? `: ${st.eventName}` : "", st.lookbackDays ? ` · ${st.lookbackDays}-day lookback` : "")), /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", {
      className: "name"
    }, "Audience"), /*#__PURE__*/React.createElement("td", null, st.include.length ? `${st.include.length} included` : "everyone triggered", st.exclude.length ? ` · ${st.exclude.length} excluded` : "")), /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", {
      className: "name"
    }, "Timing"), /*#__PURE__*/React.createElement("td", null, st.sendImmediately ? "immediately" : `${Number(st.delayDays) || 0}d ${Number(st.delayHours) || 0}h ${Number(st.delayMinutes) || 0}m after the trigger`)), /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", {
      className: "name"
    }, "Email"), /*#__PURE__*/React.createElement("td", null, "\"", st.subject || "—", "\" from ", st.fromName || "—", " ", st.fromEmail ? `<${st.fromEmail}>` : "")))), /*#__PURE__*/React.createElement("p", {
      className: "territory-hint",
      style: {
        margin: "10px 0"
      }
    }, "Each contact receives this automation once ever. Contacts without an opt-in record are treated as opted in."), /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        gap: "8px",
        alignItems: "center"
      }
    }, /*#__PURE__*/React.createElement(Button, {
      disabled: st.saving,
      onClick: () => this.save(false)
    }, st.saving ? "Saving…" : editing ? "Save changes" : "Save draft"), (!editing || st.status !== "Running") && (st.confirmActivate ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
      style: {
        fontSize: "13px"
      }
    }, "Start sending to newly triggered contacts?"), /*#__PURE__*/React.createElement(Button, {
      themeColor: "primary",
      disabled: st.saving,
      onClick: () => this.save(true)
    }, st.saving ? "Saving…" : "Yes, activate"), /*#__PURE__*/React.createElement(Button, {
      disabled: st.saving,
      onClick: () => this.setState({
        confirmActivate: false
      })
    }, "Cancel")) : /*#__PURE__*/React.createElement(Button, {
      themeColor: "primary",
      disabled: st.saving,
      onClick: () => this.setState({
        confirmActivate: true
      })
    }, "Save & activate"))))));
  }
}
export default withRouter(EmailAutomationEditorComponent);